<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { marked } from 'marked';
import { useAppStore } from '@/stores/app';
import { useAsyncData } from '@/hooks/async-data';
import { $fetch } from '@/plugins/fetch';
import { statsPv } from '@/utils';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
});

const router = useRouter();
const appStore = useAppStore();

marked.use({
  gfm: true,
  breaks: true,
});

const { data: detail } = useAsyncData<Record<string, any>>(
  'insight:article',
  () => $fetch('/insight/issue/v1/article', { data: { articleId: props.id } }),
  {
    cache: false,
    watch: [() => props.id],
    onAfterHandle({ handleSuccess, serverContext }) {
      if (serverContext) {
        if (handleSuccess && detail.value) {
          serverContext.title = `${detail.value.title} - ${detail.value.issue.issueNumber} - ${detail.value.issue.title}`;
        }
        else {
          // 如果请求出错直接服务器重定向到 404 页面
          serverContext.redirect = '/404';
        }
      }
      else {
        if (handleSuccess && detail.value) {
          appStore.setTitle(`${detail.value.title} - ${detail.value.issue.issueNumber} - ${detail.value.issue.title}`);

          // 统计埋点: 因标题是异步获取的，所以 pv 要延迟统计
          statsPv();
        }
        else {
          router.replace('/404');
        }
      }
    },
  },
);

const markedContent = computed(() => {
  return detail.value?.content ? marked.parse(detail.value.content) : '';
});
</script>

<template>
  <main class="page-insight-article relative mt-48 <md:mt-32">
    <img class="absolute left-0 top-0 translate-[-45%,-50%] -z-1 <md:hidden" src="@/assets/journalism/circle.svg">

    <div class="mx-auto min-h-1000px w-1360px <2xl:w-1200px <xl:w-full <sm:px-16 <xl:px-24">
      <template v-if="detail">
        <div class="crumbs">
          <ul class="m-0 list-none p-0">
            <li class="list-none text-16 text-hex-9a9ea6 font-normal leading-24 <md:break-all <md:text-14">
              <RouterLink class="text-hex-9a9ea6 no-underline hover:text-hex-1180ff" to="/insight">
                <span>开源态势洞察</span>
              </RouterLink>
              <span class="mx-8">/</span>
              <RouterLink class="text-hex-9a9ea6 no-underline hover:text-hex-1180ff" :to="`/insight/issue/${detail.issue.issueId}`">
                <span class="crumbs-list-title">《{{ detail.issue.title }}》{{ detail.issue.issueNumber }}</span>
              </RouterLink>
              <span class="mx-8">/</span>
              <span>{{ detail.title }}</span>
            </li>
          </ul>
        </div>

        <div class="news-header mx-auto mt-96 w-full <md:mt-48">
          <h3 class="m-0 text-center text-balance text-40 text-hex-2d2d2d font-medium leading-60 <md:text-28 <md:leading-40">
            {{ detail.title }}
          </h3>
        </div>

        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="detail-content mx-auto mt-96 w-960 break-all pb-80 text-hex-2d2d2d <md:mt-48 <md:w-full <md:pb-48" v-html="markedContent"></div>
      </template>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.page-insight-article {
  .detail-content:deep() {
    h1,
    h2 {
      font-weight: 500;
      font-size: 24px;
    }

    p,
    li {
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
    }

    img {
      display: block;
      max-width: 100%;
      object-fit: contain;
    }

    a {
      color: #1180ff;
      text-decoration: none;
    }
  }
}
</style>
